import React, { useEffect, useState } from "react";
import ApiClient from "../api/ApiClient";

/**
 * "Shares" card for the Model Spreadsheet page. Lets the logged-in user
 * create / edit / delete shares for the currently selected model. The list
 * of columns is sourced from the latest computed spreadsheet so users can
 * pick columns by their visible label (e.g. "A", "C", "D").
 *
 * Two new optional props power the "Only this exactly calculation" pin:
 *   * `viewMode` — the parent ModelSpreadsheet's viewMode ("original" |
 *     "edited"). Used to pick the default pin target when the user is
 *     already viewing their edited sibling.
 *   * `currentCalculationId` — when the user is in Edited mode this is
 *     the edited sibling's DB id; when in Original mode it is the
 *     original's DB id. Pre-fills the pin so a user editing a
 *     calculation can pin the share to that exact calculation in one
 *     click.
 */
export const ModelShares = ({
  selectedModelId,
  latestColumns,
  viewMode,
  currentCalculationId,
}) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);

  const [editingId, setEditingId] = useState(null); // null = creating new
  const [editName, setEditName] = useState("");
  const [editUserIds, setEditUserIds] = useState([]);
  const [editSharedColumns, setEditSharedColumns] = useState([]);
  // Share-all-columns toggle: when true, every column of the calculation
  // is exposed to the recipient (no SharedColumns filter on the read
  // path). On the wire this is signalled by sending
  // `SharedColumns: null` — the server's `null` list is the
  // single source of truth for "all columns" (see Migration_070
  // + the ModelShareController comment). The picker is still rendered
  // for context, but is disabled while the toggle is on.
  const [editShareAllColumns, setEditShareAllColumns] = useState(false);
  // Pin-to-one-calculation state. When `editPinToCalculation` is true the
  // share is scoped to a single calculation row; `editPinnedCalculationId`
  // carries the DB id (pre-filled from the currently-loaded calculation
  // when the user is in Edited mode).
  const [editPinToCalculation, setEditPinToCalculation] = useState(false);
  const [editPinnedCalculationId, setEditPinnedCalculationId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ApiClient.getUsersBriefList()
      .then((u) => setUsers(u || []))
      .catch((err) => console.error("Failed to load users", err));
  }, []);

  useEffect(() => {
    if (!selectedModelId) {
      setShares([]);
      return;
    }
    loadShares(selectedModelId);
    // Reset the form when switching models.
    cancelEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModelId]);

  async function loadShares(modelId) {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiClient.listModelShares(modelId);
      setShares(data || []);
    } catch (err) {
      console.error("Failed to load shares", err);
      setError({
        message: err.message || "Unknown error",
        status: err.status || null,
      });
      setShares([]);
    } finally {
      setLoading(false);
    }
  }

  function startCreate() {
    setEditingId(0); // 0 = creating
    setEditName("");
    setEditUserIds([]);
    // "Share all columns" is the more permissive default — most users
    // who click + New share probably want the recipient to see
    // everything. The columns picker stays editable, and the user can
    // untick + pick specific columns if they prefer.
    const hasColumns = (latestColumns || []).length > 0;
    setEditShareAllColumns(hasColumns);
    if (hasColumns) {
      // We still keep an empty list while the toggle is on; the
      // column picker is disabled so the user can't pick while the
      // "all" mode is active.
      setEditSharedColumns([]);
    } else {
      // No columns to choose from — fall back to picking whatever is
      // available (first column) so the share is valid out-of-the-box.
      const cols = (latestColumns || []).map((c) => c.Label).filter(Boolean);
      setEditSharedColumns(cols.length > 0 ? [cols[0]] : []);
    }
    // Default the pin to the currently-loaded calculation when in
    // Edited mode (so the user can share "this exact edited copy" in
    // one click) — the checkbox stays unchecked in Original mode
    // because pinning to a calculation that is NOT the edited sibling
    // is rarely what the user wants there.
    if (viewMode === "edited" && currentCalculationId) {
      setEditPinToCalculation(true);
      setEditPinnedCalculationId(currentCalculationId);
    } else {
      setEditPinToCalculation(false);
      setEditPinnedCalculationId(currentCalculationId || null);
    }
  }

  function startEdit(share) {
    setEditingId(share.Id);
    setEditName(share.Name || "");
    setEditUserIds([...(share.UserIds || [])]);
    // "Share all columns" is signalled on the wire by `SharedColumns`
    // being null — there's no separate boolean flag. A non-null array
    // (even an empty one, though the controller rejects that) means
    // "only these labels". Initialise the form state accordingly.
    if (share.SharedColumns == null) {
      setEditShareAllColumns(true);
      setEditSharedColumns([]);
    } else {
      setEditShareAllColumns(false);
      setEditSharedColumns([...share.SharedColumns]);
    }
    // Preserve the persisted pin state when editing an existing share.
    // Pre-fill with the currently-loaded calculation id when the share
    // is not already pinned (so flipping the checkbox on uses the calc
    // the user is looking at), otherwise keep the share's own pin.
    if (typeof share.SpreadsheetCalculationId === "number") {
      setEditPinToCalculation(true);
      setEditPinnedCalculationId(share.SpreadsheetCalculationId);
    } else {
      setEditPinToCalculation(false);
      setEditPinnedCalculationId(currentCalculationId || null);
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
    setEditUserIds([]);
    setEditSharedColumns([]);
    setEditShareAllColumns(false);
    setEditPinToCalculation(false);
    setEditPinnedCalculationId(null);
  }

  async function saveShare() {
    if (!selectedModelId) return;
    if (!editName.trim()) {
      alert("Name is required");
      return;
    }
    if (editUserIds.length === 0) {
      alert("Pick at least one user");
      return;
    }
    // Either "share all columns" must be ticked OR at least one
    // column label must be selected. The server validates this too
    // (and rejects the ambiguous "both set" case with 400) but we
    // surface a friendly client-side alert first.
    if (!editShareAllColumns && editSharedColumns.length === 0) {
      alert("Pick at least one column, or check Share all columns");
      return;
    }
    if (editShareAllColumns && editSharedColumns.length > 0) {
      // Auto-correct: turning "all columns" on while a column list is
      // selected would be rejected by the server. Clear the list
      // silently so the user does not get a confusing error.
      setEditSharedColumns([]);
    }
    // Sanity-check the pin: if the checkbox is on but the id is empty
    // (no current calculation loaded + Original mode + user didn't enter
    // anything else), reject rather than send a malformed payload.
    let pinnedId = null;
    if (editPinToCalculation) {
      const candidate =
        editPinnedCalculationId != null
          ? editPinnedCalculationId
          : currentCalculationId;
      if (!candidate || candidate <= 0) {
        alert(
          "Pin to one calculation is on but no calculation id is available. " +
            "Load a calculation first or uncheck the box."
        );
        return;
      }
      pinnedId = candidate;
    }

    const body = {
      ModelId: parseInt(selectedModelId, 10),
      Name: editName.trim(),
      UserIds: editUserIds,
      // "Share all columns" is signalled by an explicit `null` rather
      // than an empty array — the controller's validator rejects
      // empty lists as ambiguous, and the spreadsheet rows endpoint
      // checks `SharedColumns == null` (not length) to decide whether
      // to apply the column-name filter. A non-null array means "only
      // these columns".
      SharedColumns: editShareAllColumns ? null : editSharedColumns,
      SpreadsheetCalculationId: pinnedId,
    };

    setSaving(true);
    setError(null);
    try {
      if (editingId && editingId > 0) {
        await ApiClient.updateModelShare(editingId, body);
      } else {
        await ApiClient.createModelShare(body);
      }
      cancelEdit();
      await loadShares(selectedModelId);
    } catch (err) {
      console.error("Failed to save share", err);
      setError({
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      });
    } finally {
      setSaving(false);
    }
  }

  async function deleteShare(share) {
    if (!window.confirm(`Delete share "${share.Name}"?`)) return;
    setError(null);
    try {
      await ApiClient.deleteModelShare(share.Id);
      await loadShares(selectedModelId);
    } catch (err) {
      console.error("Failed to delete share", err);
      setError({
        message: err.message || "Unknown error",
        status: err.status || null,
      });
    }
  }

  function toggleUser(uid) {
    setEditUserIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  }

  function toggleColumn(label) {
    if (!label) return;
    setEditSharedColumns((prev) =>
      prev.includes(label)
        ? prev.filter((x) => x !== label)
        : [...prev, label]
    );
  }

  if (!selectedModelId) return null;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Shares</h5>
        {editingId == null && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={startCreate}
            disabled={(latestColumns || []).length === 0}
            title={
              (latestColumns || []).length === 0
                ? "Calculate the spreadsheet first so columns are available"
                : "Create a new share for this model"
            }
          >
            + New share
          </button>
        )}
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger">
            <div>{error.message}</div>
            {error.status && (
              <small className="text-muted">HTTP Status: {error.status}</small>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-muted">Loading shares…</div>
        ) : shares.length === 0 ? (
          <div className="text-muted">
            No shares for this model yet. Click <em>+ New share</em> to share
            specific columns with specific users.
          </div>
        ) : (
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Actions</th>
                <th>Name</th>
                <th>Users</th>
                <th>Columns</th>
                <th>Scope</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {shares.map((s) => (
                <tr key={s.Id}>
                  <td>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => startEdit(s)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteShare(s)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                  <td>{s.Name}</td>
                  <td>{(s.UserIds || []).length}</td>
                  <td>
                    {s.SharedColumns == null ? (
                      <span
                        className="badge badge-success"
                        style={{ color: "#000" }}
                        title="Every column of the calculation is exposed to the recipient — SharedColumns is null on the row, so the column-name filter is skipped on the read path."
                      >
                        All columns
                      </span>
                    ) : (
                      s.SharedColumns.map((c) => (
                        <span
                          key={c}
                          className="badge badge-info mr-1"
                          style={{ marginRight: 4, color: "#000" }}
                        >
                          {c}
                        </span>
                      ))
                    )}
                  </td>
                  <td>
                    {typeof s.SpreadsheetCalculationId === "number" ? (
                      <span
                        className="badge badge-warning"
                        style={{ color: "#000" }}
                        title={`Pinned to calculation #${s.SpreadsheetCalculationId} — the recipient only ever sees that one calculation. The share is dropped automatically if the calculation row is deleted.`}
                      >
                        Pinned #{s.SpreadsheetCalculationId}
                      </span>
                    ) : (
                      <span className="text-muted">Any calc</span>
                    )}
                  </td>
                  <td>{formatDateTime(s.CreateTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editingId != null && (
          <div className="mt-3 border-top pt-3">
            <h6>{editingId > 0 ? "Edit share" : "New share"}</h6>
            <div className="mb-2">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={256}
                placeholder="e.g. Daily signals for analysts"
              />
            </div>

            <div className="mb-2">
              <label className="form-label">
                Recipients ({editUserIds.length} selected)
              </label>
              <div
                className="border rounded p-2"
                style={{ maxHeight: 180, overflowY: "auto" }}
              >
                {users.length === 0 ? (
                  <div className="text-muted">Loading users…</div>
                ) : (
                  users.map((u) => (
                    <div key={u.Id} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`share-user-${u.Id}`}
                        checked={editUserIds.includes(u.Id)}
                        onChange={() => toggleUser(u.Id)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`share-user-${u.Id}`}
                      >
                        {u.Name || u.Email} {u.Email ? `(${u.Email})` : ""}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mb-2 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="share-all-columns"
                checked={editShareAllColumns}
                onChange={(e) => {
                  const next = e.target.checked;
                  setEditShareAllColumns(next);
                  // Mutually exclusive with the column list — flip the
                  // checkbox on clears the list (and the picker is
                  // disabled while on), flip off re-enables the picker.
                  if (next) {
                    setEditSharedColumns([]);
                  }
                }}
                disabled={(latestColumns || []).length === 0 && !editShareAllColumns}
              />
              <label
                className="form-check-label"
                htmlFor="share-all-columns"
                title="When checked, the recipient sees every column of the calculation. Uncheck to pick a specific subset."
              >
                Share all columns
              </label>
            </div>

            <div className="mb-2">
              <label className="form-label">
                Columns from latest spreadsheet{" "}
                {editShareAllColumns ? (
                  <span className="badge badge-info ml-1">all columns will be shared</span>
                ) : (
                  <span>({editSharedColumns.length} selected)</span>
                )}
              </label>
              {(latestColumns || []).length === 0 ? (
                <div className="text-muted">
                  No columns available yet — run a calculation first.
                </div>
              ) : (
                <div
                  className="border rounded p-2"
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    opacity: editShareAllColumns ? 0.5 : 1,
                    pointerEvents: editShareAllColumns ? "none" : "auto",
                  }}
                  aria-disabled={editShareAllColumns}
                >
                  {latestColumns.map((c) => (
                    <div key={c.Index} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`share-col-${c.Index}`}
                        checked={
                          editShareAllColumns
                            ? true
                            : editSharedColumns.includes(c.Label)
                        }
                        disabled={editShareAllColumns}
                        onChange={() => toggleColumn(c.Label)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`share-col-${c.Index}`}
                        title={c.Title || ""}
                      >
                        <strong>{c.Label}</strong>
                        {c.Title ? ` · ${c.Title}` : ""}
                        {c.Tag ? (
                          <span className="badge badge-info ml-1" style={{ marginLeft: 6 }}>
                            {c.Tag}
                          </span>
                        ) : null}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-2 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="share-pin-to-calculation"
                checked={editPinToCalculation}
                onChange={(e) => {
                  setEditPinToCalculation(e.target.checked);
                  // Snap the pin id to the currently-loaded calculation
                  // when the user flips the checkbox on, so they don't
                  // have to type/pick a separate id. They can still
                  // override it via the numeric input below.
                  if (e.target.checked && currentCalculationId) {
                    setEditPinnedCalculationId(currentCalculationId);
                  }
                }}
                disabled={!currentCalculationId && !editPinnedCalculationId}
              />
              <label
                className="form-check-label"
                htmlFor="share-pin-to-calculation"
                title="When checked, the share is pinned to one specific calculation and the recipient only sees that one. When unchecked, the share exposes its columns from every calculation of the model (legacy behaviour)."
              >
                Only this exactly calculation
              </label>
            </div>
            {editPinToCalculation && (
              <div className="mb-2">
                <label className="form-label">Pinned calculation id</label>
                <input
                  type="number"
                  className="form-control"
                  value={editPinnedCalculationId ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditPinnedCalculationId(
                      v === "" ? null : parseInt(v, 10)
                    );
                  }}
                  min={1}
                  placeholder="DB id of the calculation row"
                />
                {currentCalculationId &&
                  editPinnedCalculationId !== currentCalculationId && (
                    <small className="text-muted">
                      Currently loaded calc is #{currentCalculationId}.
                    </small>
                  )}
              </div>
            )}

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveShare}
                disabled={saving}
              >
                {saving ? "Saving…" : editingId > 0 ? "Save changes" : "Create share"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function formatDateTime(value) {
  if (value == null) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

export default ModelShares;